import { useEffect, useRef, useState } from "react";

import { WHATSAPP_AUTO_LIVE } from "../content";
import { prefersReducedMotion, useInView } from "../hooks";
import { MOCK_CATEGORIES, MOCK_ELIM_PLAYERS } from "../mockData";
import { publicAsset } from "../publicAsset";
import { MockFigure, PhoneFrame } from "./frames";
import "./LockScreenMock.css";

/**
 * M8 · Pantalla de bloqueo del jugador (Diego Salazar) el sábado del torneo.
 * Los avisos llegan por WhatsApp y como notificación de la app. Textos con el
 * formato real del backend:
 * - WhatsApp: «{título}. {cuerpo}» de `send_match_whatsapp_notice` y del
 *   sorteo (`matches/services.py`), con `reminder_heading` de `reminders.py`.
 * - App: título «Rol de juego» y «{encabezado}: vs {rival} — {horario}» de
 *   `notify_match_schedule`.
 */

const RIVAL = MOCK_ELIM_PLAYERS.aguilar;
const CATEGORY = MOCK_CATEGORIES[0];
/** Formato de `match_schedule_summary`: «17 oct, 09:00, Cancha 1». */
const SLOT = "17 oct, 09:00, Cancha 1";

type Channel = "whatsapp" | "app";

interface LockNotice {
  channel: Channel;
  when: string;
  title: string;
  lines: string[];
}

const NOTICES: LockNotice[] = [
  WHATSAPP_AUTO_LIVE
    ? {
        channel: "whatsapp",
        when: "ahora",
        title: "Torneos123",
        lines: ["Tu partido empieza pronto: hoy a las 09:00.", `vs ${RIVAL} — ${SLOT}`],
      }
    : {
        channel: "app",
        when: "ahora",
        title: "Rol de juego",
        lines: [`Tu partido empieza pronto: hoy a las 09:00: vs ${RIVAL} — ${SLOT}`],
      },
  {
    channel: "app",
    when: "ayer",
    title: "Rol de juego",
    lines: [`Recordatorio — tu partido es mañana: vs ${RIVAL} — ${SLOT}`],
  },
  WHATSAPP_AUTO_LIVE
    ? {
        channel: "whatsapp",
        when: "mié",
        title: "Torneos123",
        lines: [`Sorteo realizado — ${CATEGORY}.`, "Ya puedes ver el cuadro y contra quién te toca."],
      }
    : {
        channel: "app",
        when: "mié",
        title: `Sorteo del cuadro realizado — ${CATEGORY}`,
        lines: ["El sorteo del cuadro se realizó bajo el estándar internacional…"],
      },
];

const FIGURE_LABEL = WHATSAPP_AUTO_LIVE
  ? "Pantalla de bloqueo del celular de un jugador con tres avisos de Torneos123: por WhatsApp, el recordatorio de su partido de hoy a las 09:00 contra Tomás Aguilar en la Cancha 1; como notificación, el recordatorio del día anterior; y por WhatsApp, el aviso de que ya salió el sorteo de su categoría."
  : "Pantalla de bloqueo del celular de un jugador con tres notificaciones de Torneos123: el recordatorio de su partido de hoy a las 09:00 contra Tomás Aguilar en la Cancha 1, el recordatorio del día anterior y el aviso de que ya salió el sorteo de su categoría.";

export function LockScreenMock({ className = "" }: { className?: string }) {
  const stackRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stackRef, "0px 0px -12% 0px");
  // Una sola vez: al salir de pantalla los avisos se quedan donde están.
  const [revealed, setRevealed] = useState(prefersReducedMotion);
  useEffect(() => {
    if (inView) setRevealed(true);
  }, [inView]);

  const appIcon = publicAsset("torneos123/icon-torneos123-192.png");

  return (
    <MockFigure
      className={`mk-lock ${className}`.trim()}
      label={FIGURE_LABEL}
    >
      <PhoneFrame className="mk-lock__phone">
        <div className="mk-lock__wall">
          <BallLines />
        </div>
        <div className="mk-lock__screen">
          <StatusIcons />
          <div className="mk-lock__clock">
            <Padlock />
            <span className="mk-lock__date">sábado 17 de octubre</span>
            <span className="mk-lock__time">8:05</span>
          </div>

          <div ref={stackRef} className={`mk-lock__stack${revealed ? " is-in" : ""}`}>
            {NOTICES.map((notice) => (
              <div
                key={`${notice.channel}-${notice.when}`}
                className={`mk-notif mk-lock__card mk-lock__card--${notice.channel}`}
              >
                <div className="mk-notif__head">
                  {notice.channel === "whatsapp" ? (
                    <ChatMark />
                  ) : (
                    <img src={appIcon} alt="" width={20} height={20} loading="lazy" decoding="async" />
                  )}
                  <span>{notice.channel === "whatsapp" ? "WhatsApp" : "Torneos123"}</span>
                  <small>{notice.when}</small>
                </div>
                <strong>{notice.title}</strong>
                <p>
                  {notice.lines.map((line, index) => (
                    <span key={line} className="mk-lock__line">
                      {index > 0 ? " " : null}
                      {line}
                    </span>
                  ))}
                </p>
              </div>
            ))}
          </div>

          <div className="mk-lock__dock">
            <span>
              <Flashlight />
            </span>
            <span>
              <Camera />
            </span>
          </div>
          <div className="mk-lock__home" />
        </div>
      </PhoneFrame>
    </MockFigure>
  );
}

/** Marca genérica de chat en verde (no es el logo oficial de WhatsApp). */
function ChatMark() {
  return (
    <svg className="mk-lock__chat-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect width="24" height="24" rx="6" fill="#25d366" />
      <path
        d="M12 5.4a6.6 6.6 0 0 0-5.7 9.95l-.95 3.25 3.35-.9A6.6 6.6 0 1 0 12 5.4Z"
        fill="none"
        stroke="#fff"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <g fill="#fff">
        <circle cx="9.2" cy="12" r="0.95" />
        <circle cx="12" cy="12" r="0.95" />
        <circle cx="14.8" cy="12" r="0.95" />
      </g>
    </svg>
  );
}

function StatusIcons() {
  return (
    <svg className="mk-lock__status" viewBox="0 0 56 12" aria-hidden="true" focusable="false">
      <g fill="currentColor">
        <rect x="0" y="8" width="3" height="4" rx="0.8" />
        <rect x="4.2" y="6" width="3" height="6" rx="0.8" />
        <rect x="8.4" y="4" width="3" height="8" rx="0.8" />
        <rect x="12.6" y="2" width="3" height="10" rx="0.8" />
      </g>
      <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M20.4 5.2a8.2 8.2 0 0 1 11.2 0" />
        <path d="M22.8 7.7a4.6 4.6 0 0 1 6.4 0" />
      </g>
      <circle cx="26" cy="10.4" r="1.3" fill="currentColor" />
      <rect x="36" y="1.5" width="16.5" height="9" rx="2.6" fill="none" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.1" />
      <rect x="37.7" y="3.2" width="11.6" height="5.6" rx="1.3" fill="currentColor" />
      <rect x="53.4" y="4.5" width="1.6" height="3" rx="0.7" fill="currentColor" fillOpacity="0.55" />
    </svg>
  );
}

function Padlock() {
  return (
    <svg className="mk-lock__padlock" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="5" y="10.5" width="14" height="10.5" rx="2.6" fill="currentColor" />
      <path d="M8.2 10.5V7.8a3.8 3.8 0 0 1 7.6 0v2.7" fill="none" stroke="currentColor" strokeWidth="2.1" />
    </svg>
  );
}

function Flashlight() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M8 2.8h8v3.4l-2.2 3.6V21h-3.6V9.8L8 6.2Z" />
      <path d="M8 6.2h8M12 13v2" />
    </svg>
  );
}

function Camera() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M4 8.2a2 2 0 0 1 2-2h2.1l1.6-2.2h4.6l1.6 2.2H18a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
      <circle cx="12" cy="13" r="3.4" />
    </svg>
  );
}

/** Pelota de tenis tenue en el fondo de pantalla. */
function BallLines() {
  return (
    <svg className="mk-lock__ball" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="2.4" />
      <path d="M17.5 17.5c13.5 13 13.5 52 0 65M82.5 17.5c-13.5 13-13.5 52 0 65" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
